/**
 * SLICE D1 — the driver wire contract, HAND-MIRRORED in TypeScript.
 *
 * 🔴 NO CROSS-TREE IMPORT (PLAN §Seams). Unlike `trips/types.ts` and
 * `alerts/types.ts`, which `import type` the api zod-inferred shapes, the driver
 * contract is hand-mirrored on each side: there is no `packages/` contract, and
 * raad-backend is building `api/src/modules/drivers/` in the SAME tree in
 * parallel. An `import type` from a module that may not exist yet would break this
 * tree's typecheck mid-build. The PLAN's Seams table names this explicitly:
 * "zod in api, hand-mirrored TS + Dart, the live-frame precedent". If the shipped
 * JSON ever disagrees with this file, that is a seam break to REPORT, not to
 * paper over.
 *
 * These are the EXACT shapes from the PLAN's "Wire contract (fixed before
 * parallel build)" block — do not widen or rename without a contract change.
 */

/** How a driver was attributed to a trip / alert / event.
 *  - `assignment` — a permanent or temporary assignment covered the instant.
 *  - `manual`     — an owner picked the driver for this trip's window. */
export type DriverSource = 'manual' | 'assignment';

/** The attribution shown on a trip row, alert row or behaviour event.
 *  `null` means UNKNOWN DRIVER — no assignment covered that instant and nobody
 *  has fixed it. It is a real state, said in words, never blanked. */
export type DriverRef = { id: string; name: string; source: DriverSource } | null;

export type AssignmentKind = 'permanent' | 'temporary' | 'manual';

/** The vehicle a driver currently holds (on the Driver row). */
export interface CurrentVehicleRef {
  id: string;
  plate: string;
  kind: 'permanent' | 'temporary';
  assignmentId: string;
  /** ISO end for a temporary assignment; `null` for a permanent (open-ended). */
  endsAt: string | null;
}

/** The driver currently assigned to a vehicle (on the vehicle hub header). */
export interface CurrentDriver {
  id: string;
  name: string;
  kind: 'permanent' | 'temporary';
  assignmentId: string;
  endsAt: string | null;
}

export interface Driver {
  id: string;
  name: string;
  /** E.164, as stored. */
  phone: string;
  licenceNo: string | null;
  notes: string | null;
  /** ISO archive time, or `null` for an active driver. Archived drivers still
   *  resolve on history — a trip they drove keeps naming them. */
  archivedAt: string | null;
  currentVehicle: CurrentVehicleRef | null;
  trips30d: number;
  harshEvents30d: number;
}

export interface Assignment {
  id: string;
  vehicleId: string;
  driverId: string;
  driverName: string;
  kind: AssignmentKind;
  startsAt: string;
  endsAt: string | null;
  /** `manual` only — the trip whose window this fix covers. */
  tripId: string | null;
  createdAt: string;
}

export interface DriversResponse {
  drivers: Driver[];
}

export interface AssignmentsResponse {
  assignments: Assignment[];
}

export interface DriverCreateBody {
  name: string;
  phone: string;
  licenceNo?: string;
  notes?: string;
}

export interface DriverPatchBody {
  name?: string;
  phone?: string;
  licenceNo?: string | null;
  notes?: string | null;
}

export interface AssignmentCreateBody {
  driverId: string;
  kind: 'permanent' | 'temporary';
  /** Optional for permanent (defaults to now server-side); required for temporary. */
  startsAt?: string;
  /** Required for `temporary`. */
  endsAt?: string;
}

/**
 * 🔴 THE DEFENSIVE ACCESSORS — the one place this module touches the imported
 * trips / alerts / vehicle shapes.
 *
 * The api adds `driver` to the trip/alert/behaviour items and `currentDriver` to
 * the vehicle items. Those shapes are imported from `api/` by their existing
 * `types.ts`, so this build sees them WITHOUT the new field until raad-backend
 * lands it. A value typed as the pre-driver shape is still assignable to an
 * OPTIONAL-field parameter, so reading the field through these helpers keeps the
 * typecheck green regardless of which side lands first, and softens an absent
 * field to the honest `null` (unknown driver / no current driver).
 */
// 🔴 GENERIC `T extends object`, NOT a weak `{ driver?: … }` param. TypeScript
// refuses to pass an object with NO overlapping key to a type whose properties
// are all optional (the "weak type" check), and a `TripListItem` that predates
// the `driver` field has no key in common with `{ driver?: DriverRef }`. The
// generic binds `T` to the concrete item type, so the call typechecks whether or
// not the imported shape has caught up; `'driver' in item` reads it safely.
export function driverOf<T extends object>(item: T | null | undefined): DriverRef {
  if (item && 'driver' in item) {
    return ((item as { driver?: DriverRef }).driver as DriverRef) ?? null;
  }
  return null;
}

export function currentDriverOf<T extends object>(vehicle: T | null | undefined): CurrentDriver | null {
  if (vehicle && 'currentDriver' in vehicle) {
    return ((vehicle as { currentDriver?: CurrentDriver | null }).currentDriver as CurrentDriver | null) ?? null;
  }
  return null;
}
